/**
 * AppLayout — navigation shell replacing AppShell.
 * Desktop: fixed sidebar (left) + top bar (logo, dark mode, user, hamburger) + page content.
 * Mobile: top bar + page content + bottom nav (5 tabs).
 *
 * Pages render into the <main> content area via Outlet.
 *
 * Desktop sidebar supports collapse (icon-only) mode:
 * - Click the chevron button at the top of the sidebar to toggle.
 * - Collapsed mode hides labels and shows tooltips on hover.
 */
import { useState, useEffect } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useVisits } from '../../hooks/useVisits';
import { useTrips } from '../../hooks/useTrips';
import { useDarkMode } from '../../providers/DarkModeProvider';
import { useAuth } from '../../contexts/AuthContext';
import { SignInButton } from '../../components/SignInButton';
import { AvatarButtonMenu } from '../../components/AvatarButtonMenu';
import { ToastContainer } from '../../layout/ToastContainer';
import {
  Home, Ticket, List, Plane, Eye, Share2, Plus,
  Sun, Moon, Settings, MapPin, Star, BarChart3,
} from 'lucide-react';

/** Inline tooltip that appears to the right of its child when the sidebar is collapsed. */
function Tooltip({ children, text, disabled }: { children: React.ReactNode; text: string; disabled: boolean }): React.ReactElement {
  if (disabled) return <>{children}</>;
  return (
    <div className="relative group w-full" style={{ width: undefined }}>
      {children}
      <div className="pointer-events-none absolute top-0 left-full ml-2 w-max px-2 py-1 rounded bg-gray-900 dark:bg-gray-100 text-white dark:text-gray-900 text-xs font-medium opacity-0 group-hover:opacity-100 transition-opacity duration-150 z-50 whitespace-nowrap">
        {text}
      </div>
    </div>
  );
}

export function AppLayout(): React.ReactElement {
  const [menuOpen, setMenuOpen] = useState(false);
  const [toasts, setToasts] = useState<{ id: string; message: string; type: 'success' | 'error' | 'info' }[]>([]);
  const navigate = useNavigate();
  const { data: visits = [] } = useVisits();
  const { data: trips = [] } = useTrips();
  const { darkMode, toggleDarkMode } = useDarkMode();
  const { isAuthenticated } = useAuth();
  const location = useLocation();
  const isAdmin = location.pathname.startsWith('/admin');
  const [adminExpanded, setAdminExpanded] = useState(isAdmin);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  // Re-open admin section when navigating into it
  useEffect(() => { setAdminExpanded(isAdmin); }, [isAdmin]);

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const _addToast = (message: string, type: 'success' | 'error' | 'info' = 'success') => {
    const id = Date.now().toString();
    setToasts(prev => [...prev, { id, message, type }]);
    setTimeout(() => setToasts(prev => prev.filter(t => t.id !== id)), 4000);
  };

  const dismissToast = (id: string) => {
    setToasts(prev => prev.filter(t => t.id !== id));
  };

  const handleNav = (path: string) => { setMenuOpen(false); navigate(path); };

  // Close menu on route change (mobile)
  useEffect(() => { if (menuOpen) setMenuOpen(false); }, [location.pathname, menuOpen]);

  const sidebarItems: Array<{
    icon: React.ComponentType<{ className?: string }>;
    label: string;
    path: string;
    color?: string;
  }> = [
    { icon: Home, label: 'Home', path: '/', color: 'text-gray-600 dark:text-gray-400' },
    { icon: Ticket, label: 'Plan Visit', path: '/create', color: 'text-disney-blue-500 dark:text-disney-blue-400' },
    { icon: List, label: `All Visits (${visits.length})`, path: '/all-visits', color: 'text-gray-600 dark:text-gray-400' },
    { icon: Plane, label: `My Trips (${trips.length})`, path: '/my-trips', color: 'text-gray-600 dark:text-gray-400' },
    { icon: Eye, label: 'Wait Times', path: '/wait-times', color: 'text-gray-600 dark:text-gray-400' },
    { icon: Share2, label: 'Shared with Me', path: '/shared', color: 'text-gray-600 dark:text-gray-400' },
  ];

  /* ---- active path ---- */
  const isActive = (path: string) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };

  return (
    <div className="min-h-[100dvh] bg-surface-light dark:bg-surface-dark">
      {/* Skip link */}
      <a href="#main-content" className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 bg-disney-blue-600 text-white px-4 py-2 rounded z-[100]">
        Skip to main content
      </a>

      {/* ===== HEADER ===== */}
      <header className="fixed top-0 inset-x-0 z-50 py-3 px-4 bg-white dark:bg-surface-dark-card border-b border-gray-100 dark:border-gray-800">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={() => setMenuOpen(!menuOpen)} className="p-2 rounded-lg bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 transition-colors md:hidden" aria-label="Toggle menu">
              <svg className="w-5 h-5 text-gray-600 dark:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                {menuOpen
                  ? <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  : <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                }
              </svg>
            </button>
            <img src="/app_icon_transparent.png" alt="Disney Queue Tracker" className="w-8 h-8" />
            <h1 className="font-display text-2xl font-bold text-gray-900 dark:text-gray-100 hidden sm:block">Disney Queue Tracker</h1>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={toggleDarkMode} className="p-2 rounded-lg bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 transition-colors" aria-label="Toggle dark mode">
              {darkMode ? <Sun className="w-5 h-5 text-yellow-500" /> : <Moon className="w-5 h-5 text-gray-600 dark:text-gray-400" />}
            </button>
            {isAuthenticated ? <AvatarButtonMenu /> : <SignInButton />}
          </div>
        </div>
      </header>

      {/* ===== MOBILE MENU OVERLAY ===== */}
      {menuOpen && (
        <div className="fixed inset-0 top-16 z-40 bg-black/30 md:hidden" onClick={() => setMenuOpen(false)} />
      )}
      <nav className={`fixed inset-y-0 left-0 z-50 w-64 bg-white dark:bg-surface-dark-card shadow-2xl transform transition-transform duration-200 ease-in-out md:hidden top-14 ${menuOpen ? 'translate-x-0' : '-translate-x-full'}`} role="navigation" aria-label="Main navigation">
        <ul className="divide-y divide-gray-200 dark:divide-gray-700 py-2">
          {sidebarItems.map(({ icon: Icon, label, path, color }) => (
            <li key={path}>
              <button onClick={() => handleNav(path)}
                className={`w-full px-4 py-3 text-left flex items-center gap-3 transition-colors
                  ${isActive(path) ? 'bg-blue-50 dark:bg-blue-900/30' : 'hover:bg-gray-50 dark:hover:bg-gray-800'}`}>
                <Icon className={`w-5 h-5 ${isActive(path) ? 'text-disney-blue-500' : color}`} />
                <span className={`font-medium ${isActive(path) ? 'text-disney-blue-600 dark:text-blue-400' : 'text-gray-700 dark:text-gray-300'}`}>{label}</span>
              </button>
            </li>
          ))}
          {/* Admin section — only visible inside /admin */}
          {isAdmin && (
            <li>
              <button onClick={() => setAdminExpanded(!adminExpanded)}
                className="w-full px-4 py-3 text-left flex items-center gap-3 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors">
                <Settings className="w-5 h-5 text-disney-blue-500" />
                <span className="font-medium text-disney-blue-600 dark:text-blue-400">Admin</span>
              </button>
              {adminExpanded && (
                <ul className="divide-y divide-gray-100 dark:divide-gray-700/50 ml-4">
                  {[
                    { icon: Star, label: 'Rating', path: '/admin/rating' },
                    { icon: BarChart3, label: 'Reports', path: '/admin/reports' },
                    { icon: BarChart3, label: 'Analytics', path: '/admin/analytics' },
                    { icon: MapPin, label: 'Parks', path: '/admin/parks' },
                  ].map(item => (
                    <li key={item.path}>
                      	<button onClick={() => { setMenuOpen(false); navigate(item.path); }}
                        className={`w-full px-4 py-2 text-left flex items-center gap-3 transition-colors
      	  	     ${isActive(item.path) ? 'bg-blue-50 dark:bg-blue-900/30' : 'hover:bg-gray-50 dark:hover:bg-gray-800'}`}>
                        <item.icon className="w-4 h-4 text-gray-500" />
                        <span className={`font-medium text-sm ${isActive(item.path) ? 'text-disney-blue-600 dark:text-blue-400' : 'text-gray-600 dark:text-gray-400'}`}>{item.label}</span>
                      	</button>
                    </li>
                  ))}
                </ul>
              )}
            </li>
          )}
        </ul>
      </nav>

      {/* ===== DESKTOP SIDEBAR ===== */}
      <aside
        className={`hidden md:flex fixed top-16 left-0 bottom-0 bg-white dark:bg-surface-dark-card border-r border-gray-200 dark:border-gray-700 z-30 flex-col transition-all duration-200 ${
          sidebarCollapsed ? 'w-16' : 'w-56'
        }`}
        role="navigation"
        aria-label="Desktop sidebar"
        // aria-expanded not supported on role="navigation" — sidebar collapse state is communicated via aria-label
      >
        {/* Collapse toggle */}
        <div className="flex items-center justify-center py-2 border-b border-gray-200 dark:border-gray-700">
          <button
            onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
            className="p-1.5 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
            aria-label={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            title={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            <svg
              className={`w-4 h-4 text-gray-500 dark:text-gray-400 transition-transform duration-200 ${sidebarCollapsed ? 'rotate-180' : ''}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 19l-7-7 7-7m8 14l-7-7 7-7" />
            </svg>
          </button>
        </div>

        <ul className={`flex-1 py-3 space-y-0.5 ${sidebarCollapsed ? 'space-y-1' : 'space-y-0.5'}`}>
          {sidebarItems.map(({ icon: Icon, label, path, color }) => (
            <li key={path} className="relative">
              {isActive(path) && (
                <span className="absolute left-0 inset-y-1 w-0.5 rounded-full bg-disney-blue-500" aria-hidden />
              )}
              <Tooltip text={label} disabled={!sidebarCollapsed}>
                <button
                  onClick={() => handleNav(path)}
                  className={`w-full flex items-center transition-colors rounded-lg mx-1
                    ${sidebarCollapsed
                      ? `justify-center px-2 py-2.5 ${isActive(path) ? 'bg-disney-blue-50 dark:bg-blue-900/20' : 'hover:bg-gray-50 dark:hover:bg-gray-800'}`
                      : `w-full px-4 py-2.5 flex items-center gap-3 ${isActive(path)
                          ? 'bg-disney-blue-50 dark:bg-blue-900/20'
                          : 'hover:bg-gray-50 dark:hover:bg-gray-800'}`}`}
                >
                  <Icon className={`flex-shrink-0 ${sidebarCollapsed ? 'w-5 h-5' : 'w-4 h-4'} ${isActive(path) ? 'text-disney-blue-500' : color}`} />
                  {!sidebarCollapsed && (
                    <span className={`text-sm font-medium truncate ${isActive(path) ? 'text-disney-blue-600 dark:text-blue-400' : 'text-gray-600 dark:text-gray-400'}`}>
                      {label}
                    </span>
                  )}
                </button>
              </Tooltip>
            </li>
          ))}
          {/* Admin section — shown as individual items when collapsed, or as a collapsible group when expanded */}
          {isAdmin && !sidebarCollapsed && (
            <li key="__admin-parent">
              <button onClick={() => setAdminExpanded(!adminExpanded)}
                className="w-full px-4 py-2.5 flex items-center gap-3 transition-colors rounded-lg mx-1
                  hover:bg-gray-50 dark:hover:bg-gray-800">
                <Settings className="w-4 h-4 flex-shrink-0 text-disney-blue-500" />
                <span className="text-sm font-medium text-disney-blue-600 dark:text-blue-400">Admin</span>
              </button>
              {adminExpanded && (
                <ul className="ml-5 mt-0.5 space-y-0.5">
                  {[
                    { icon: Star, label: 'Rating', path: '/admin/rating' },
                    { icon: BarChart3, label: 'Reports', path: '/admin/reports' },
                    { icon: BarChart3, label: 'Analytics', path: '/admin/analytics' },
                    { icon: MapPin, label: 'Parks', path: '/admin/parks' },
                  ].map(item => (
                    <li key={item.path}>
                      	<button onClick={() => handleNav(item.path)}
                        className={`w-full px-4 py-1.5 flex items-center gap-3 transition-colors rounded-lg
      	  	     ${isActive(item.path)
                            ? 'bg-blue-50 dark:bg-blue-900/30'
                            : 'hover:bg-gray-50 dark:hover:bg-gray-800'}`}>
                        <item.icon className="w-3.5 h-3.5 text-gray-500" />
                        <span className={`text-xs font-medium ${isActive(item.path) ? 'text-disney-blue-600 dark:text-blue-400' : 'text-gray-600 dark:text-gray-400'}`}>{item.label}</span>
                      	</button>
                    </li>
                  ))}
                </ul>
              )}
            </li>
          )}
          {isAdmin && sidebarCollapsed && (
            <>
              {[
                { icon: Star, label: 'Rating', path: '/admin/rating' },
                { icon: BarChart3, label: 'Reports', path: '/admin/reports' },
                { icon: BarChart3, label: 'Analytics', path: '/admin/analytics' },
                { icon: MapPin, label: 'Parks', path: '/admin/parks' },
              ].map(item => (
                <li key={item.path} className="relative">
                  {isActive(item.path) && (
                    <span className="absolute left-0 inset-y-1 w-0.5 rounded-full bg-disney-blue-500" aria-hidden />
                  )}
                  <Tooltip text={item.label} disabled={false}>
                    <button
                      onClick={() => handleNav(item.path)}
                      className={`w-full flex items-center transition-colors rounded-lg mx-1 justify-center px-2 py-2.5 ${
                        isActive(item.path) ? 'bg-disney-blue-50 dark:bg-blue-900/20' : 'hover:bg-gray-50 dark:hover:bg-gray-800'
                      }`}>
                      <item.icon className="w-5 h-5 flex-shrink-0 text-gray-500" />
                    </button>
                  </Tooltip>
                </li>
              ))}
            </>
          )}
        </ul>
      </aside>

      {/* ===== PAGE CONTENT ===== */}
      <main id="main-content" className={`min-h-[100dvh] pt-[60px] md:pt-[70px] ${sidebarCollapsed ? 'md:ml-[4rem]' : 'md:ml-[14rem]'}`}>
        <div className="max-w-7xl mx-auto px-4 py-6 animate-fade-in">
          <Outlet />
        </div>
      </main>

      {/* ===== MOBILE BOTTOM NAV ===== */}
      <nav className="fixed bottom-0 inset-x-0 z-40 bg-white dark:bg-surface-dark-card border-t border-gray-200 dark:border-gray-700 md:hidden" role="navigation" aria-label="Bottom navigation">
        <ul className="flex items-center justify-around py-1">
          {[
            { icon: Home, label: 'Home', path: '/' },
            { icon: Plane, label: 'Trips', path: '/my-trips' },
            { icon: Plus, label: 'Plan', path: '/create' },
            { icon: Eye, label: 'Wait', path: '/wait-times' },
            { icon: Share2, label: 'Me', path: '/shared' },
          ].map(({ icon: Icon, label, path }) => (
            <li key={path}>
              <button onClick={() => handleNav(path)}
                className={`relative flex flex-col items-center py-1 px-3 transition-colors ${
                  isActive(path) ? 'text-disney-blue-500' : 'text-gray-400 dark:text-gray-500'}`}>
                {isActive(path) && (
                  <span className="absolute -top-1 left-1/2 -translate-x-1/2 w-1 h-1 rounded-full bg-disney-blue-500" aria-hidden />
                )}
                <Icon className="w-5 h-5" />
                <span className={`text-[10px] mt-0.5 font-medium ${isActive(path) ? 'text-disney-blue-500' : ''}`}>{label}</span>
              </button>
            </li>
          ))}
        </ul>
      </nav>

      {/* ===== TOASTS ===== */}
      <div className="fixed bottom-20 right-4 z-50 space-y-2 md:bottom-6 md:right-6">
        <ToastContainer toasts={toasts} onDismiss={dismissToast} />
      </div>
    </div>
  );
}
